import React, { forwardRef, useEffect, useMemo } from 'react';
import ReactSelect from 'react-select';
import { isAccountScopedPortalUser } from '../pages/accountAccess';

const PRIMARY_ACCOUNT_FIELD_NAMES = new Set([
  'account_name',
  'accountname',
  'audit_account',
  'company_name',
  'companyname',
  'review-account',
]);

const readCurrentPortalUser = () => {
  if (typeof window === 'undefined') return null;

  try {
    return JSON.parse(window.sessionStorage.getItem('user_token'));
  } catch (error) {
    return null;
  }
};

const getOptionValue = (option) => String(option?.value ?? '').trim();

/**
 * Shared react-select presentation boundary.
 *
 * Menus render in document.body by default so they cannot be clipped by a
 * card, modal, table scroller, or page section with constrained overflow.
 * Callers can still override every menu prop, including opting out with an
 * explicit `menuPortalTarget={null}`.
 */
const PortalSelect = forwardRef(({
  className,
  classNamePrefix,
  inputId,
  isDisabled,
  menuPlacement = 'auto',
  menuPortalTarget,
  menuPosition,
  menuShouldScrollIntoView = false,
  name,
  onChange,
  options,
  styles,
  value,
  ...props
}, ref) => {
  const defaultPortalTarget = typeof document !== 'undefined'
    ? document.body
    : undefined;
  const resolvedPortalTarget = menuPortalTarget === undefined
    ? defaultPortalTarget
    : menuPortalTarget;
  const resolvedMenuPosition = menuPosition === undefined && resolvedPortalTarget
    ? 'fixed'
    : menuPosition;
  const isEntitySelector = /(account|company|location)/i.test(
    `${inputId || ''} ${name || ''}`,
  );
  const useDefaultEntityPresentation = isEntitySelector && !classNamePrefix;
  const normalizedFieldName = String(inputId || name || '').trim().toLowerCase();
  const isPrimaryAccountSelector = PRIMARY_ACCOUNT_FIELD_NAMES.has(normalizedFieldName);
  const singleVendorAccountOption = useMemo(() => {
    if (!isPrimaryAccountSelector || !Array.isArray(options)) return null;

    const accessibleOptions = options.filter((option) => getOptionValue(option));

    return accessibleOptions.length === 1 && isAccountScopedPortalUser(readCurrentPortalUser())
      ? accessibleOptions[0]
      : null;
  }, [isPrimaryAccountSelector, options]);
  const selectedValue = getOptionValue(value);
  const singleVendorAccountValue = getOptionValue(singleVendorAccountOption);

  useEffect(() => {
    if (
      singleVendorAccountOption
      && selectedValue !== singleVendorAccountValue
      && typeof onChange === 'function'
    ) {
      onChange(singleVendorAccountOption);
    }
  }, [
    onChange,
    selectedValue,
    singleVendorAccountOption,
    singleVendorAccountValue,
  ]);

  const resolvedClassName = [
    className,
    useDefaultEntityPresentation ? 'mvp-entity-select' : '',
  ].filter(Boolean).join(' ') || undefined;
  const resolvedClassNamePrefix = classNamePrefix
    || (useDefaultEntityPresentation ? 'mvp-entity-select' : undefined);
  const customMenuPortalStyle = styles?.menuPortal;
  const mergedStyles = {
    ...styles,
    menuPortal: (base, state) => {
      const portalBase = { ...base, zIndex: 20000 };
      return typeof customMenuPortalStyle === 'function'
        ? customMenuPortalStyle(portalBase, state)
        : portalBase;
    },
  };

  return (
    <ReactSelect
      ref={ref}
      {...props}
      className={resolvedClassName}
      classNamePrefix={resolvedClassNamePrefix}
      inputId={inputId}
      isDisabled={Boolean(isDisabled || singleVendorAccountOption)}
      menuPlacement={menuPlacement}
      menuPortalTarget={resolvedPortalTarget}
      menuPosition={resolvedMenuPosition}
      menuShouldScrollIntoView={menuShouldScrollIntoView}
      name={name}
      onChange={onChange}
      options={options}
      styles={mergedStyles}
      value={value}
    />
  );
});

PortalSelect.displayName = 'PortalSelect';

export default PortalSelect;
